<template>
    <div class="v2-tab-content">
        <VisitFilters heading="Produtos" :filters="filters" :fields="fields" @filtrar="filtrar" @limpar="limpar">
            <template v-if="editable || canRefreshErp" slot="actions">
                <a-button v-if="canRefreshErp" :loading="refreshingErp" @click="$emit('refresh-erp')">
                    <i v-if="!refreshingErp" class="fa-solid fa-rotate"></i>
                    Atualizar dados do ERP
                </a-button>
                <a-button v-if="editable" type="primary" @click="showAdd = true">
                    <i class="fa-solid fa-plus"></i>
                    Adicionar produto
                </a-button>
            </template>
        </VisitFilters>

        <div class="page-section">
            <a-card :bordered="false" class="table-card">
                <div class="table-container">
                    <a-table
                        size="small"
                        row-key="id"
                        :row-selection="rowSelection"
                        :columns="columns"
                        :data-source="lines"
                        :loading="loading"
                        :pagination="pagination"
                        :scroll="{ x: 'max-content' }"
                        :locale="{ emptyText: editable ? 'Nenhum produto nesta requisição.' : 'Nenhum produto ofertado nesta requisição.' }"
                        @change="tableChange"
                    >
                        <template slot="product" slot-scope="text, record">
                            <div class="pro-name">{{ record.product.description }}</div>
                            <div v-if="productInfo(record)" class="pro-ean">{{ productInfo(record) }}</div>
                            <a-tag v-if="compact" class="pro-origin" :color="lineStatusOf(record).color">{{ lineStatusOf(record).label }}</a-tag>
                            <a-tag class="pro-origin" :color="originOf(record).color">{{ originOf(record).label }}</a-tag>
                            <a-tag v-if="regimeOf(record)" class="pro-origin" :color="regimeOf(record).color">{{ regimeOf(record).label }}</a-tag>
                        </template>
                        <template slot="lineStatus" slot-scope="text, record">
                            <a-tag :color="lineStatusOf(record).color">{{ lineStatusOf(record).label }}</a-tag>
                        </template>
                        <template v-for="field in editableFields" :slot="field" slot-scope="text, record">
                            <input
                                v-if="isActiveCell(record, field)"
                                :key="`input-${field}`"
                                ref="activeInput"
                                type="text"
                                :class="['ant-input', 'line-input', `line-input-${field}`, { saved: savedCells[cellKey(record, field)] }]"
                                :maxlength="field === 'observation' ? 250 : 14"
                                v-model="activeValue"
                                @input="handleInput($event, record, field)"
                                @blur="commitCell(record, field)"
                                @keydown="handleKeydown($event, record, field)"
                            />
                            <span
                                v-else
                                :key="`display-${field}`"
                                :class="['ant-input', 'line-input', 'line-display', `line-input-${field}`, { saved: savedCells[cellKey(record, field)], prefilled: isPrefilled(record, field), locked: isLocked(record, field) }]"
                                :title="isLocked(record, field) ? 'O vendedor informou que este produto está em falta.' : field === 'observation' ? displayValue(record, field) : null"
                                @click="activateCell(record, field)"
                            >{{ displayValue(record, field) }}</span>
                        </template>
                        <template slot="acoes" slot-scope="text, record">
                            <ActionMenu :actions="lineActions(record)" />
                        </template>
                        <template slot="offer" slot-scope="text, record">
                            <div class="col-stack">
                                <span class="col-main">{{ sellerPrice(record) }}</span>
                                <span class="col-sub">Qtd. {{ sellerQuantity(record) }}</span>
                                <template v-if="record.last_purchase">
                                    <span class="col-sub col-sub-wrap">Últ.&nbsp;compra {{ brl(record.last_purchase.price) }}</span>
                                    <span v-if="deltaOf(record, record.last_purchase.price)" class="col-sub" :class="deltaOf(record, record.last_purchase.price).cls">
                                        {{ deltaOf(record, record.last_purchase.price).text }}
                                    </span>
                                </template>
                                <span v-else class="col-sub">Últ. compra —</span>
                            </div>
                        </template>
                        <template slot="lastOffer" slot-scope="text, record">
                            <div v-if="record.last_offer" class="col-stack">
                                <span class="col-main">{{ brl(record.last_offer.price) }}</span>
                                <span class="col-sub">Qtd. {{ num(record.last_offer.quantity) }}</span>
                                <span v-if="deltaOf(record, record.last_offer.price)" class="col-sub" :class="deltaOf(record, record.last_offer.price).cls">
                                    {{ deltaOf(record, record.last_offer.price).text }}
                                </span>
                            </div>
                            <span v-else>--</span>
                        </template>
                        <template slot="erpStock" slot-scope="text, record">
                            <div class="qty-labeled">
                                <span class="qty-label">Est.</span>
                                <a-tooltip v-if="erpOf(record) && erpOf(record).pending" :title="`Pendente de entrega: ${num(erpOf(record).pending)}`">
                                    <span class="col-main erp-hint">{{ erpNumber(record, "stock") }}</span>
                                </a-tooltip>
                                <span v-else class="col-main">{{ erpNumber(record, "stock") }}</span>
                                <span class="qty-label">Sug.</span>
                                <a-tooltip v-if="erpOf(record) && erpOf(record).coverage_days !== null" :title="`Cobertura prevista: ${num(erpOf(record).coverage_days)} dias`">
                                    <span class="erp-hint">{{ erpNumber(record, "suggestion") }}</span>
                                </a-tooltip>
                                <span v-else>{{ erpNumber(record, "suggestion") }}</span>
                            </div>
                        </template>
                        <template slot="erpGiro" slot-scope="text, record">
                            <QvGiroSparkline
                                v-if="erpOf(record) && erpOf(record).giro !== null"
                                :value="erpOf(record).giro"
                                :periods="giroPeriods(record)"
                                :tendency="erpOf(record).trend"
                            />
                            <span v-else>--</span>
                        </template>
                        <template slot="erpCriticality" slot-scope="text, record">
                            <span
                                v-if="erpOf(record) && erpOf(record).criticality"
                                class="erp-status"
                                :style="{ background: stockStatus(record).bg, color: stockStatus(record).color }"
                            >{{ stockStatus(record).label }}</span>
                            <span v-else>--</span>
                        </template>
                        <template slot="erpLastCost" slot-scope="text, record">
                            <a-tooltip v-if="erpOf(record) && erpOf(record).average_cost !== null" :title="`Custo médio: ${brl(erpOf(record).average_cost)}`">
                                <span class="erp-hint">{{ erpMoney(record, "last_cost") }}</span>
                            </a-tooltip>
                            <span v-else>{{ erpMoney(record, "last_cost") }}</span>
                        </template>
                        <template slot="total" slot-scope="text, record">
                            <div class="col-stack">
                                <span class="col-main">{{ lineTotal(record) }}</span>
                                <span v-if="hasErp" class="col-sub" :class="marginClass(record)">Margem {{ marginText(record) }}</span>
                            </div>
                        </template>
                    </a-table>
                </div>
            </a-card>
        </div>

        <transition name="slide-up">
            <div v-if="selectedLines.length && !finished" class="selection-footer">
                <div class="selection-footer-info">
                    <span class="selection-footer-count">{{ selectedLines.length }}</span>
                    {{ selectedLines.length === 1 ? "produto selecionado" : "produtos selecionados" }}
                </div>
                <div class="selection-footer-actions">
                    <a-button size="large" :disabled="bulkBusy" @click="clearSelection">
                        <span class="material-icons-round">clear</span>
                        Limpar Seleção
                    </a-button>
                    <a-button v-if="!hasErp || canRefreshErp" size="large" :loading="refreshingErp" :disabled="!!bulkBusy || refreshingErp" @click="$emit('refresh-erp', selectedLines)">
                        <i v-if="!refreshingErp" class="fa-solid fa-rotate"></i>
                        {{ hasErp ? "Atualizar dados do ERP" : "Atualizar estoque/custo" }}
                    </a-button>
                    <template v-if="editable">
                        <a-button size="large" :loading="bulkBusy === 'unskip'" :disabled="!!bulkBusy" @click="bulkSkip(false)">
                            <i class="fa-solid fa-check"></i>
                            Marcar como ofertados
                        </a-button>
                        <a-button size="large" type="danger" :loading="bulkBusy === 'remove'" :disabled="!!bulkBusy" @click="bulkRemove">
                            <i class="fa-solid fa-trash"></i>
                            Remover Selecionados
                        </a-button>
                    </template>
                </div>
            </div>
        </transition>

        <AddProductModal v-if="showAdd" :order="order" :client-owned="clientOwned" @added="$emit('line-added', $event)" @close="showAdd = false" />
    </div>
</template>

<script>
import visitFormat from "@/pages/visits/visit-format.mixin";
import VisitFilters from "@/pages/visits/components/visit-filters.vue";
import ActionMenu from "@/components/popovers/action-menu.popover.vue";
import SellerOrderService from "@/services/v3/seller-orders/seller-order.service";
import { loaderMixin } from "@/mixins/sweet-loader.mixin";
import { emptyFiltersOf, filterParamsOf, filterFieldsOf } from "@/pages/visits/visit-list.mixin";
import AddProductModal from "../modals/add-product.modal.vue";
import { FINAL_STATUSES, LINE_ORIGINS, LINE_STATUS, PURCHASE_REGIMES } from "../seller-order-status";
import { suggestionCodeKey } from "@/services/MiraiService";
import QvGiroSparkline from "@/pages/monitors/winners-v2/components/modals/product-quick-view/_qv-giro-sparkline.vue";
import { getStockStatus } from "@/pages/monitors/winners-v2/components/modals/product-quick-view/stockStatus";
import { observedWidthMixin } from "@/mixins/observed-width.mixin";

const WIDTHS = {
    selection: 40,
    product: 280,
    productCompact: 220,
    packing: 64,
    status: 100,
    criticality: 88,
    stock: 80,
    giro: 90,
    lastCost: 85,
    offer: 130,
    lastOffer: 125,
    quantity: 90,
    price: 115,
    total: 95,
    observation: 180,
    actions: 50,
};

const sum = (widths) => widths.reduce((total, width) => total + width, 0);

const EDITABLE_FIELDS = ["quantity", "price", "observation"];

const HORIZONTAL_FIELDS = ["quantity", "price"];

const toText = (value) => (value === null || value === undefined ? "" : String(value).replace(".", ","));

const toNumber = (text) => {
    const value = parseFloat(String(text || "").replace(",", "."));
    return Number.isFinite(value) ? value : null;
};

export const effectiveOf = (record, field) => {
    const own = record[`client_${field}`];
    return own === null || own === undefined ? record[field] : own;
};

const ORIGIN_OPTIONS = Object.keys(LINE_ORIGINS).map((value) => ({ value, label: LINE_ORIGINS[value].label }));

const REGIME_DEFINITION = {
    key: "purchaseRegime",
    type: "select",
    multiple: true,
    label: "Tipo",
    param: "purchase_regime",
    options: Object.keys(PURCHASE_REGIMES).map((value) => ({ value, label: PURCHASE_REGIMES[value].label })),
};

const STATUS_OPTIONS = Object.keys(LINE_STATUS).map((value) => ({ value, label: LINE_STATUS[value].label }));

const DEFINITIONS = [
    { key: "description", type: "text", label: "Nome do produto", param: "description", span: 5 },
    { key: "ean", type: "text", digits: true, label: "EAN", param: "ean" },
    { key: "internalCode", type: "text", label: "Código interno", param: "internal_code" },
    { key: "origin", type: "select", label: "Origem", param: "origin", options: ORIGIN_OPTIONS },
    { key: "status", type: "select", multiple: true, label: "Status", param: "status", options: STATUS_OPTIONS },
    { key: "packing", type: "text", label: "Embalagem", param: "packing", advanced: true },
    { key: "quantity", type: "range", label: "Qtd. sugerida", params: ["quantity_min", "quantity_max"], advanced: true },
    { key: "price", type: "range", label: "Preço ofertado (R$)", params: ["price_min", "price_max"], advanced: true },
    { key: "amount", type: "range", label: "Total (R$)", params: ["amount_min", "amount_max"], advanced: true },
    { key: "lastOffer", type: "range", label: "Última oferta (R$)", params: ["last_offer_price_min", "last_offer_price_max"], advanced: true },
    { key: "observation", type: "text", label: "Observação", param: "observation", advanced: true },
];

const CRITICALITY_OPTIONS = ["RUPTURA", "NEGATIVO", "CRITICO", "BAIXO", "NORMAL", "EXCESSO", "SEM_VENDA"].map((value) => ({
    value,
    label: getStockStatus(value).label,
}));

const ERP_DEFINITIONS = [
    { key: "erpCriticality", type: "select", multiple: true, label: "Situação do estoque", param: "erp_criticality", options: CRITICALITY_OPTIONS, advanced: true },
    { key: "erpStock", type: "range", label: "Estoque", params: ["erp_stock_min", "erp_stock_max"], advanced: true },
    { key: "erpGiro", type: "range", label: "Giro (un/dia)", params: ["erp_giro_min", "erp_giro_max"], advanced: true },
    { key: "erpSuggestion", type: "range", label: "Sugestão", params: ["erp_suggestion_min", "erp_suggestion_max"], advanced: true },
    { key: "erpMargin", type: "range", label: "Margem (%)", params: ["erp_margin_min", "erp_margin_max"], advanced: true },
];

const ALL_DEFINITIONS = [...DEFINITIONS, REGIME_DEFINITION, ...ERP_DEFINITIONS];

const GIRO_PERIODS = [
    { key: "90d", label: "Últimos 90 dias", days: 90 },
    { key: "30d", label: "Últimos 30 dias", days: 30 },
    { key: "14d", label: "Últimos 14 dias", days: 14 },
    { key: "7d", label: "Últimos 7 dias", days: 7 },
];

const defaultFilters = (order) => ({ ...emptyFiltersOf(ALL_DEFINITIONS), status: order && order.status === "APPROVED" ? ["OFFERED"] : [] });

export const defaultLinesQuery = (order) => ({ ...filterParamsOf(DEFINITIONS, defaultFilters(order)), page: 1, per_page: 20 });

export default {
    name: "SellerOrderProductsTab",
    components: { VisitFilters, ActionMenu, AddProductModal, QvGiroSparkline },
    mixins: [visitFormat, loaderMixin, observedWidthMixin],
    props: {
        order: { type: Object, required: true },
        lines: { type: Array, default: () => [] },
        meta: { type: Object, default: null },
        erp: { type: Object, default: () => ({}) },
        refreshingErp: { type: Boolean, default: false },
        hasErp: { type: Boolean, default: false },
        erpRefreshable: { type: Boolean, default: false },
        loading: { type: Boolean, default: false },
        editable: { type: Boolean, default: false },
        clientOwned: { type: Boolean, default: false },
    },
    data() {
        return {
            service: new SellerOrderService(),
            filters: defaultFilters(this.order),
            editableFields: EDITABLE_FIELDS,
            activeCell: null,
            activeValue: "",
            drafts: {},
            savedCells: {},
            pendingSaves: [],
            originals: {},
            page: 1,
            pageSize: 20,
            sort: { key: null, dir: null },
            appliedParams: filterParamsOf(DEFINITIONS, defaultFilters(this.order)),
            selectedRowKeys: [],
            bulkBusy: null,
            showAdd: false,
        };
    },
    computed: {
        hasRegime() {
            return this.lines.some((line) => "purchase_regime" in line);
        },
        definitions() {
            return [...DEFINITIONS, ...(this.hasRegime ? [REGIME_DEFINITION] : []), ...(this.hasErp ? ERP_DEFINITIONS : [])];
        },
        fields() {
            return filterFieldsOf(this.definitions);
        },
        canRefreshErp() {
            return this.hasErp && this.erpRefreshable;
        },
        finished() {
            return FINAL_STATUSES.includes(this.order.status);
        },
        separateWidth() {
            const working = [WIDTHS.stock, ...(this.hasErp ? [WIDTHS.giro] : []), WIDTHS.quantity, WIDTHS.price, WIDTHS.offer, WIDTHS.total];
            return sum([WIDTHS.selection, WIDTHS.product, WIDTHS.packing, WIDTHS.status, ...working, WIDTHS.actions]);
        },
        compact() {
            return !!this.observedWidth && this.observedWidth < this.separateWidth;
        },
        rowSelection() {
            return {
                columnWidth: WIDTHS.selection,
                selectedRowKeys: this.finished ? [] : this.selectedRowKeys,
                onChange: (keys) => {
                    if (!this.finished) this.selectedRowKeys = keys;
                },
                getCheckboxProps: () => ({ props: { disabled: this.finished } }),
            };
        },
        selectedLines() {
            const keys = new Set(this.selectedRowKeys);
            return this.lines.filter((line) => keys.has(line.id));
        },
        currentPage() {
            if (this.meta) return this.meta.current_page;
            const pages = Math.max(1, Math.ceil(this.lines.length / this.pageSize));
            return Math.min(this.page, pages);
        },
        pageRows() {
            if (this.meta) return this.lines;
            const start = (this.currentPage - 1) * this.pageSize;
            return this.lines.slice(start, start + this.pageSize);
        },
        pagination() {
            const total = this.meta ? this.meta.total : this.lines.length;
            return {
                current: this.currentPage,
                pageSize: this.pageSize,
                total,
                showSizeChanger: true,
                pageSizeOptions: ["20", "50", "100"],
                locale: { items_per_page: "/ página" },
                showTotal: (count) => `${count} ${count === 1 ? "produto" : "produtos"}`,
            };
        },
        erpColumns() {
            const sortable = this.sortable;
            if (!this.hasErp) {
                const stock = { title: "Estoque", key: "stock", width: WIDTHS.stock, align: "center", customRender: (t, row) => this.erpValue(row, "stock") };
                const lastCost = { title: "Últ. Custo", key: "last_cost", width: WIDTHS.lastCost, align: "right", customRender: (t, row) => this.erpValue(row, "last_cost") };
                return { all: [stock, lastCost], working: [stock], rest: [lastCost] };
            }
            const criticality = { title: "Situação", ...sortable("erp_criticality"), width: WIDTHS.criticality, align: "center", scopedSlots: { customRender: "erpCriticality" } };
            const stock = { title: "Estoque", ...sortable("erp_stock"), width: WIDTHS.stock, align: "center", scopedSlots: { customRender: "erpStock" } };
            const giro = { title: "Giro", ...sortable("erp_giro"), width: WIDTHS.giro, align: "center", scopedSlots: { customRender: "erpGiro" } };
            const lastCost = { title: "Últ. Custo", ...sortable("erp_last_cost"), width: WIDTHS.lastCost, align: "right", scopedSlots: { customRender: "erpLastCost" } };
            return { all: [criticality, stock, giro, lastCost], working: [stock, giro], rest: [criticality, lastCost] };
        },
        columns() {
            const sortable = this.sortable;
            const editable = this.editable;
            const product = { title: "Produto", ...sortable("description"), fixed: "left", width: this.compact ? WIDTHS.productCompact : WIDTHS.product, scopedSlots: { customRender: "product" } };
            const packing = { title: "Emb", ...sortable("packing"), width: WIDTHS.packing, align: "center", customRender: (t, row) => row.product.packing || "--" };
            const status = { title: "Status", ...sortable("status"), width: WIDTHS.status, scopedSlots: { customRender: "lineStatus" } };
            const offer = { title: "Oferta", ...sortable("price"), width: WIDTHS.offer, align: "center", scopedSlots: { customRender: "offer" } };
            const lastOffer = { title: "Última oferta", ...sortable("last_offer_price"), width: WIDTHS.lastOffer, align: "center", scopedSlots: { customRender: "lastOffer" } };
            const quantity = {
                title: "Qtd. pedida",
                key: "client_quantity",
                width: WIDTHS.quantity,
                align: "center",
                ...(editable ? { scopedSlots: { customRender: "quantity" } } : { customRender: (t, row) => this.num(effectiveOf(row, "quantity")) }),
            };
            const price = {
                title: "Preço pedido",
                key: "client_price",
                width: WIDTHS.price,
                align: editable ? "center" : "right",
                ...(editable ? { scopedSlots: { customRender: "price" } } : { customRender: (t, row) => this.brl(effectiveOf(row, "price")) }),
            };
            const total = { title: "Total", ...sortable("amount"), width: WIDTHS.total, align: "center", scopedSlots: { customRender: "total" } };
            const observation = {
                title: "Observação",
                ...sortable("observation"),
                width: WIDTHS.observation,
                ...(editable ? { scopedSlots: { customRender: "observation" } } : { customRender: (t, row) => row.observation || "--" }),
            };
            const actions = { title: "Ação", key: "acoes", align: "center", fixed: "right", width: WIDTHS.actions, scopedSlots: { customRender: "acoes" } };
            const head = [product, ...(this.compact ? [] : [packing, status])];
            const erp = this.erpColumns;
            if (editable) return [...head, ...erp.working, quantity, price, offer, total, ...erp.rest, lastOffer, observation, actions];
            return [...head, ...erp.all, offer, lastOffer, quantity, price, total, observation, actions];
        },
    },
    watch: {
        finished(value) {
            if (value) this.clearSelection();
        },
    },
    methods: {
        sortable(key) {
            return this.meta ? { key, sorter: true, sortDirections: ["descend", "ascend"], sortOrder: this.sort.key === key ? `${this.sort.dir}end` : false } : { key };
        },
        tableChange(pagination, _filters, sorter) {
            const key = sorter && sorter.order ? sorter.columnKey : null;
            const dir = sorter && sorter.order === "ascend" ? "asc" : "desc";
            const changed = key !== this.sort.key || (key && dir !== this.sort.dir) || pagination.pageSize !== this.pageSize;
            this.sort = { key, dir: key ? dir : null };
            this.page = changed ? 1 : pagination.current;
            this.pageSize = pagination.pageSize;
            if (this.meta) this.emitQuery();
        },
        emitQuery() {
            this.$emit("query", {
                ...this.appliedParams,
                page: this.page,
                per_page: this.pageSize,
                sort: this.sort.key ? `${this.sort.dir === "asc" ? "" : "-"}${this.sort.key}` : undefined,
            });
        },
        erpValue(record, field) {
            const entry = this.erp[suggestionCodeKey(record.product.internal_code)];
            if (!entry) return "--";
            if (entry.loading) return "...";
            const value = entry[field];
            if (value === null || value === undefined) return "--";
            return field === "last_cost" ? this.brl(value) : this.num(value);
        },
        erpOf(record) {
            return record.erp || null;
        },
        erpNumber(record, field) {
            const erp = this.erpOf(record);
            return erp && erp[field] !== null && erp[field] !== undefined ? this.num(erp[field]) : "--";
        },
        erpMoney(record, field) {
            const erp = this.erpOf(record);
            return erp && erp[field] !== null && erp[field] !== undefined ? this.brl(erp[field]) : "--";
        },
        stockStatus(record) {
            return getStockStatus(this.erpOf(record).criticality);
        },
        giroPeriods(record) {
            const periods = this.erpOf(record).giro_periods || {};
            return GIRO_PERIODS.map(({ key, label, days }) => ({ label, giro: periods[key], quantity: (periods[key] || 0) * days }));
        },
        marginOf(record) {
            const erp = this.erpOf(record);
            if (!erp) return null;
            if (!(this.cellKey(record, "price") in this.drafts)) return erp.margin;
            const price = this.valueOf(record, "price");
            return price === null || !erp.sale_price ? null : ((erp.sale_price - price) / erp.sale_price) * 100;
        },
        marginText(record) {
            const value = this.marginOf(record);
            if (value === null || value === undefined) return "--";
            return `${value.toLocaleString("pt-BR", { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;
        },
        marginClass(record) {
            const value = this.marginOf(record);
            if (value === null || value === undefined || value === 0) return "";
            return value < 0 ? "vpm-up" : "vpm-down";
        },
        sellerQuantity(record) {
            return record.quantity === null || record.quantity === undefined ? "--" : this.num(record.quantity);
        },
        sellerPrice(record) {
            return record.price === null || record.price === undefined ? "--" : this.brl(record.price);
        },
        isPrefilled(record, field) {
            if (!["price", "quantity"].includes(field) || this.cellKey(record, field) in this.drafts) return false;
            const own = record[`client_${field}`];
            return (own === null || own === undefined) && record[field] !== null && record[field] !== undefined;
        },
        clearSelection() {
            this.selectedRowKeys = [];
        },
        runBulk(kind, lines, request) {
            this.bulkBusy = kind;
            return lines
                .reduce((chain, line) => chain.then(() => request(line)), Promise.resolve())
                .catch((error) => this.notifyError(error, "Não foi possível atualizar todos os produtos selecionados."))
                .finally(() => {
                    this.bulkBusy = null;
                    this.clearSelection();
                });
        },
        bulkSkip(skipped) {
            const lines = this.selectedLines.filter((line) => (line.status === "SKIPPED") !== skipped);
            if (!lines.length) {
                this.clearSelection();
                return;
            }
            this.runBulk(skipped ? "skip" : "unskip", lines, (line) =>
                this.service.updateProduct(this.order.id, line.id, { skipped }).then(({ data }) => this.$emit("line-saved", data))
            );
        },
        bulkRemove() {
            const lines = this.selectedLines;
            if (!lines.length) return;
            this.confirm_action({
                message: `Remover ${lines.length} ${lines.length === 1 ? "produto" : "produtos"} da requisição?`,
                confirm_text: "Sim, remover",
                cancel_text: "Voltar",
                callback: () => {
                    this.runBulk("remove", lines, (line) =>
                        this.service
                            .removeProduct(this.order.id, line.id)
                            .then(({ data }) => this.$emit("line-removed", { id: line.id, totals: data.totals }))
                    );
                },
            });
        },
        filtrar() {
            this.page = 1;
            this.clearSelection();
            this.appliedParams = filterParamsOf(this.definitions, this.filters);
            this.emitQuery();
        },
        limpar() {
            this.filters = emptyFiltersOf(ALL_DEFINITIONS);
            this.filtrar();
        },
        lineStatusOf(record) {
            return LINE_STATUS[record.status] || { label: record.status || "--", color: "" };
        },
        lineTotal(record) {
            const drafting = ["price", "quantity"].some((field) => this.cellKey(record, field) in this.drafts);
            if (!drafting && record.amount !== undefined) return record.amount === null ? "--" : this.brl(record.amount);
            const price = this.valueOf(record, "price");
            const quantity = this.valueOf(record, "quantity");
            return price === null || quantity === null ? "--" : this.brl(price * quantity * this.packageUnits(record));
        },
        packageUnits(record) {
            if (this.order.box_pricing) return 1;
            const units = parseFloat(String((record.product && record.product.packing) || "").trim().split(/\s+/)[1]);
            return units > 0 ? units : 1;
        },
        cellKey(record, field) {
            return `${record.id}:${field}`;
        },
        rawText(record, field) {
            return field === "observation" ? record.observation || "" : toText(effectiveOf(record, field));
        },
        valueOf(record, field) {
            const key = this.cellKey(record, field);
            return key in this.drafts ? toNumber(this.drafts[key]) : effectiveOf(record, field);
        },
        displayValue(record, field) {
            const key = this.cellKey(record, field);
            if (field === "observation") return key in this.drafts ? this.drafts[key] : record.observation || "";
            const value = this.valueOf(record, field);
            if (value === null || value === undefined) return "";
            return field === "price" ? this.brl(value) : this.num(value);
        },
        isActiveCell(record, field) {
            return this.activeCell === this.cellKey(record, field);
        },
        isLocked(record, field) {
            return record.status === "SHORTAGE" && field !== "observation";
        },
        activateCell(record, field) {
            if (this.isLocked(record, field)) return;
            const key = this.cellKey(record, field);
            this.originals[key] = this.rawText(record, field);
            this.activeValue = key in this.drafts ? this.drafts[key] : this.originals[key];
            this.activeCell = key;
            this.$emit("cell-focus", record, this.pageRows);
            this.$nextTick(() => {
                const refs = this.$refs.activeInput;
                const el = Array.isArray(refs) ? refs[0] : refs;
                if (el) {
                    el.focus();
                    el.select();
                }
            });
        },
        handleInput(event, record, field) {
            let value = event.target.value;
            if (field !== "observation") {
                value = value.replace(/\./g, ",").replace(/[^\d,]/g, "");
                const comma = value.indexOf(",");
                if (comma !== -1) {
                    value = value.slice(0, comma + 1) + value.slice(comma + 1).replace(/,/g, "").slice(0, field === "price" ? 2 : 3);
                }
                event.target.value = value;
            }
            this.activeValue = value;
            this.$set(this.drafts, this.cellKey(record, field), value);
        },
        handleKeydown(event, record, field) {
            if (event.key === "Escape") {
                this.$delete(this.drafts, this.cellKey(record, field));
                this.activeCell = null;
                return;
            }
            if (["ArrowLeft", "ArrowRight"].includes(event.key)) {
                const target = HORIZONTAL_FIELDS[HORIZONTAL_FIELDS.indexOf(field) + (event.key === "ArrowLeft" ? -1 : 1)];
                if (!HORIZONTAL_FIELDS.includes(field) || !target) return;
                event.preventDefault();
                this.activateCell(record, target);
                return;
            }
            if (!["Enter", "ArrowDown", "ArrowUp"].includes(event.key)) return;
            event.preventDefault();
            const rows = this.pageRows;
            const step = event.key === "ArrowUp" ? -1 : 1;
            let index = rows.findIndex((row) => row.id === record.id) + step;
            while (rows[index] && this.isLocked(rows[index], field)) index += step;
            if (rows[index]) this.activateCell(rows[index], field);
            else event.target.blur();
        },
        commitCell(record, field) {
            const key = this.cellKey(record, field);
            if (key in this.drafts) {
                const text = this.drafts[key];
                if (text === this.originals[key]) this.$delete(this.drafts, key);
                else this.saveLine(record, { [field]: field === "observation" ? text.trim() || null : toNumber(text) }, [key]);
            }
            if (this.activeCell === key) this.activeCell = null;
        },
        flush() {
            const el = document.activeElement;
            if (this.activeCell && el && el.blur) el.blur();
            return this.$nextTick()
                .then(() => Promise.all(this.pendingSaves))
                .then((results) => results.every(Boolean));
        },
        saveLine(record, payload, keys = []) {
            const request = this.persistLine(record, payload, keys);
            this.pendingSaves.push(request);
            return request.finally(() => {
                this.pendingSaves = this.pendingSaves.filter((item) => item !== request);
            });
        },
        persistLine(record, payload, keys) {
            return this.service
                .updateProduct(this.order.id, record.id, payload)
                .then(({ data }) => {
                    keys.forEach((key) => this.$set(this.savedCells, key, true));
                    this.$emit("line-saved", data);
                    return true;
                })
                .catch((error) => {
                    keys.forEach((key) => this.$delete(this.savedCells, key));
                    this.notifyError(error, "Não foi possível salvar o produto.");
                    return false;
                })
                .finally(() => keys.forEach((key) => this.$delete(this.drafts, key)));
        },
        lineActions(record) {
            const giro = {
                label: "Análise de giro",
                icon: "query_stats",
                iconClass: "action-info",
                onClick: () => this.$emit("giro", record, this.pageRows),
            };
            if (!this.editable) return [giro];
            return [
                giro,
                record.status === "SKIPPED" && {
                    label: "Marcar como ofertado",
                    icon: "check_circle",
                    onClick: () => this.saveLine(record, { skipped: false }),
                },
                {
                    label: "Remover",
                    icon: "delete",
                    onClick: () => this.removeLine(record),
                },
            ].filter(Boolean);
        },
        removeLine(record) {
            this.confirm_action({
                message: "Remover este produto da requisição?",
                subtitle: record.product.description,
                confirm_text: "Sim, remover",
                cancel_text: "Voltar",
                callback: () => {
                    this.service
                        .removeProduct(this.order.id, record.id)
                        .then(({ data }) => this.$emit("line-removed", { id: record.id, totals: data.totals }))
                        .catch((error) => this.notifyError(error, "Não foi possível remover o produto."));
                },
            });
        },
        productInfo(record) {
            const { ean, internal_code: code, packing } = record.product;
            return [ean && `EAN ${ean}`, code && `Cód. ${code}`, this.compact && packing && `Emb ${packing}`].filter(Boolean).join(" · ");
        },
        regimeOf(record) {
            return PURCHASE_REGIMES[record.purchase_regime] || null;
        },
        originOf(record) {
            return LINE_ORIGINS[record.origin] || { label: record.origin || "--", color: "" };
        },
        deltaOf(record, reference) {
            if (!reference || !record.price) return null;
            const diff = record.price - reference;
            const percent = Math.abs((diff / reference) * 100).toLocaleString("pt-BR", { minimumFractionDigits: 1, maximumFractionDigits: 1 });
            if (Math.abs(diff) < 0.005) return { text: "= 0,0%", cls: "" };
            const sign = diff > 0 ? "+" : "−";
            return { text: `${sign}${this.brl(Math.abs(diff))} · ${sign}${percent}%`, cls: diff > 0 ? "vpm-up" : "vpm-down" };
        },
    },
};
</script>

<style lang="scss" scoped>
@import "@/pages/visits/_page";

.line-input {
    display: inline-block;
    box-sizing: border-box;
    height: 28px;
    text-align: center;
}

.line-input-quantity {
    width: 70px;
}

.line-input-price {
    width: 100px;
}

.line-input-observation {
    width: 160px;
    text-align: left;
}

.table-container {
    max-height: none;
    overflow-y: visible;
}

.table-container ::v-deep .ant-table-thead > tr > th {
    background-color: #ffffff !important;
}

.pro-name {
    white-space: normal;
}

.pro-ean {
    font-size: 11px;
    color: #8c8c8c;
}

.pro-origin {
    margin-top: 2px;
}

.col-stack {
    display: flex;
    flex-direction: column;
    align-items: center;
    line-height: 1.3;
}

.col-main {
    white-space: nowrap;
}

.col-sub {
    color: rgba(0, 0, 0, 0.45);
    white-space: nowrap;
}

.col-sub-wrap {
    white-space: normal;
    text-align: center;
}

.qty-labeled {
    display: inline-grid;
    grid-template-columns: auto auto;
    column-gap: 6px;
    line-height: 1.3;
    text-align: left;
    white-space: nowrap;
}

.qty-label {
    font-size: 0.9em;
    color: rgba(0, 0, 0, 0.45);
}

.erp-hint {
    cursor: help;
    border-bottom: 1px dotted #bfbfbf;
}

.erp-status {
    display: inline-block;
    padding: 0 8px;
    border-radius: 4px;
    font-size: 12px;
    font-weight: 600;
    line-height: 22px;
    white-space: nowrap;
}

.line-input.saved {
    background-color: #13c7213d;
    color: green !important;
}

.line-display.prefilled {
    color: #8c8c8c;
}

.line-display.locked {
    color: #bfbfbf;
    cursor: not-allowed;
}

.line-display {
    cursor: text;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
}

.vpm-up {
    color: #cf1322;
    font-weight: 600;
}

.vpm-down {
    color: #389e0d;
    font-weight: 600;
}

.selection-footer {
    position: fixed;
    left: 50%;
    bottom: 14px;
    transform: translateX(-50%);
    z-index: 999;
    width: calc(100% - 32px);
    max-width: 70vw;
    min-width: max-content;
    background: #22252a;
    border-radius: 10px;
    padding: 8px 16px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 14px;
    box-shadow:
        0 8px 24px rgba(0, 0, 0, 0.24),
        0 2px 8px rgba(0, 0, 0, 0.16);
}

.selection-footer-info {
    font-size: 0.74rem;
    color: rgba(255, 255, 255, 0.68);
    white-space: nowrap;
}

.selection-footer-count {
    font-size: 0.9rem;
    font-weight: 700;
    color: #ffffff;
}

.selection-footer-actions {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-shrink: 0;
    white-space: nowrap;

    .ant-btn {
        height: 34px;
        padding: 0 16px;
        display: flex;
        align-items: center;
        gap: 6px;
        border: 0;
        border-radius: 8px;
        font-size: 0.8rem;
        font-weight: 600;
        transition: all 0.2s;

        &:hover,
        &:focus {
            background: #ff5a47;
            color: #ffffff;
        }
    }
}
</style>
